import React from "react";
import MarketingPage, { MarketingSection, Pillar } from "@/apps/rider/components/MarketingPage";

export default function RiderGuidelines() {
  return (
    <MarketingPage
      eyebrow="Rider guidelines"
      title="The rules of the ride."
      lede="A few simple expectations that keep Link's commute experience consistent for every rider and every driver. Most of these will feel obvious — they're written down so we can hold the bar."
      back="/"
      backLabel="Home"
      testId="rider-guidelines-page"
      cta={{ to: "/help", label: "Visit help center" }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Pillar index={1} title="Be ready at pickup" testId="rg-pillar-ready">
          Pickup windows are tight by design. Step outside one to two minutes before your scheduled time so other riders downstream stay on schedule.
        </Pillar>
        <Pillar index={2} title="Keep payment active" testId="rg-pillar-payment">
          Charges run before service — single-day at confirmation, weekly plans every Monday at 6 AM Central. If a card declines, you'll get a 24-hour heads-up to swap it before the ride is paused.
        </Pillar>
        <Pillar index={3} title="Treat drivers like teammates" testId="rg-pillar-respect">
          Drivers see you most days. Common courtesy isn't optional — it's the foundation. Repeated complaints from drivers may result in account suspension at our discretion.
        </Pillar>
        <Pillar index={4} title="Keep the ride on-route" testId="rg-pillar-route">
          Link is point-to-point. Drivers can't add stops, run errands, or detour outside the booked route. If you need a different route, edit the route from your dashboard before the ride.
        </Pillar>
      </div>

      <MarketingSection eyebrow="Pickup readiness" title="Three minutes is everything." testId="rg-readiness">
        <p>
          Once your pickup window opens, drivers can wait up to <strong className="text-[#0A1128]">three minutes</strong> before continuing on. That keeps downstream riders on time. A no-show after three minutes is treated as a missed ride and is non-refundable.
        </p>
        <p>
          Need a few extra minutes one morning? Tap the active ride card from <a href="/account" className="text-[#ed6500] underline-offset-4 hover:underline">Account</a> and message the driver — most drivers can hold a few minutes for a known regular.
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="Cancellations" title="Three hours, no charge." testId="rg-cancellations">
        <p>
          You can cancel a single upcoming trip up to <strong className="text-[#0A1128]">three hours before pickup</strong> with no charge. Inside the three-hour window, the trip is non-refundable so the driver can be released to other commitments.
        </p>
        <p>
          Subscription cancellations end at the close of the current paid week — already-paid weeks stay bookable. <a href="/legal/terms" className="text-[#ed6500] underline-offset-4 hover:underline">Read the full policy →</a>
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="Conduct" title="What we won't tolerate." testId="rg-conduct">
        <ul className="list-disc pl-5 space-y-2">
          <li>Smoking, vaping, or open alcohol in the vehicle.</li>
          <li>Verbal or physical aggression toward drivers or other passengers.</li>
          <li>Damaging the vehicle (cleaning fees apply, plus immediate suspension review).</li>
          <li>Asking for off-route stops or unauthorized detours.</li>
          <li>Bringing additional unscheduled passengers into the ride.</li>
        </ul>
        <p>
          Patterns of any of the above will result in account review and, if needed, permanent suspension. Riders are notified before suspension takes effect except in emergency cases.
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="Items, pets, & accessibility" title="A few practical notes." testId="rg-items">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-[#0A1128]">Personal items:</strong> a backpack, briefcase, or single grocery run is welcome. Larger loads need a heads-up — message support so we can route you to a driver with the room.</li>
          <li><strong className="text-[#0A1128]">Service animals:</strong> always welcome, no notice required.</li>
          <li><strong className="text-[#0A1128]">Pets:</strong> small carrier-only pets are fine; please flag pet rides at booking so the driver expects it.</li>
          <li><strong className="text-[#0A1128]">Wheelchair-accessible vehicles:</strong> in-progress. <a href="/legal/accessibility" className="text-[#ed6500] underline-offset-4 hover:underline">Read our accessibility statement →</a></li>
        </ul>
      </MarketingSection>
    </MarketingPage>
  );
}
